import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { launchEndDate } from "@/lib/launchDate";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * PineForge.AI — Pricing cards (Starter / Pro only, monthly / yearly toggle).
 *
 * All user-facing strings are i18n-driven via react-i18next; plan pricing
 * (numbers) and PLAN_IDS remain hard-coded here as business config.
 */

// Business data — NOT translated (numbers, ids, dates).
export const PLAN_DATA = {
  starter: {
    id: "starter",
    credits: 100,
    monthly: { standard: 39, launch: 29 },
    yearly:  { standard: 348, launch: 108, equivalentMonthly: 9, equivalentMonthlyStandard: 29 },
    highlighted: false,
  },
  pro: {
    id: "pro",
    credits: 500,
    monthly: { standard: 69, launch: 39 },
    yearly:  { standard: 588, launch: 348, equivalentMonthly: 29, equivalentMonthlyStandard: 49 },
    highlighted: true,
  },
};

// Backwards compat re-export (tests / call-sites still import PLANS).
export const PLANS = PLAN_DATA;

function useLaunchActive() {
  const [active, setActive] = useState(() => Date.now() < launchEndDate().getTime());
  useEffect(() => {
    const id = setInterval(() => {
      setActive(Date.now() < launchEndDate().getTime());
    }, 30_000);
    return () => clearInterval(id);
  }, []);
  return active;
}

function useBillingEnabled() {
  const [enabled, setEnabled] = useState(false);
  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API}/billing/status`)
      .then((r) => { if (!cancelled) setEnabled(Boolean(r?.data?.enabled)); })
      .catch(() => { if (!cancelled) setEnabled(false); });
    return () => { cancelled = true; };
  }, []);
  return enabled;
}

function PriceBlock({ plan, cycle, launchActive }) {
  const { t } = useTranslation();
  const isMonthly = cycle === "monthly";
  const block = isMonthly ? plan.monthly : plan.yearly;

  if (isMonthly) {
    const displayed = launchActive ? block.launch : block.standard;
    return (
      <div className="mb-5">
        <div className="flex items-baseline gap-2">
          <span className="text-4xl sm:text-5xl font-semibold tracking-tighter text-white tabular-nums">
            €{displayed}
          </span>
          <span className="text-zinc-500 text-sm">{t("pricing_cards.per_month_slash")}</span>
        </div>
        <p className="text-[10px] font-mono uppercase tracking-wider text-zinc-500 mt-0.5">
          {t("glossary.excl_vat")}
        </p>
        {launchActive && (
          <div className="flex items-center flex-wrap gap-2 mt-2 text-xs">
            <span className="text-zinc-500 tabular-nums">
              <span className="text-[10px] font-mono uppercase tracking-wider text-zinc-500 mr-1">{t("pricing_cards.regular_price")}</span>
              <span className="line-through decoration-zinc-700">€{block.standard} {t("pricing_cards.per_month_slash")}</span>
            </span>
            <span className="inline-flex items-center px-1.5 py-0.5 rounded font-mono uppercase tracking-wider bg-emerald-500/10 border border-emerald-500/25 text-emerald-300 text-[10px]">
              {t("pricing_cards.launch_price")}
            </span>
          </div>
        )}
        <p className="text-sm font-mono text-blue-400 mt-3">{t("pricing_cards.credits_per_month_short", { n: plan.credits })}</p>
      </div>
    );
  }

  const equivalentMonthly = block.equivalentMonthly;
  const displayedAnnual = launchActive ? block.launch : block.standard;
  const standardMonthly = block.equivalentMonthlyStandard;
  return (
    <div className="mb-5">
      <div className="flex items-baseline gap-2">
        <span className="text-4xl sm:text-5xl font-semibold tracking-tighter text-white tabular-nums">
          €{equivalentMonthly}
        </span>
        <span className="text-zinc-500 text-sm">{t("pricing_cards.per_month_slash")}</span>
      </div>
      <p className="text-[10px] font-mono uppercase tracking-wider text-zinc-500 mt-0.5">
        {t("glossary.excl_vat")}
      </p>
      <p className="text-xs text-zinc-400 mt-1.5 tabular-nums">
        {t("pricing_cards.billed_annually_at")}{" "}
        <span className="text-zinc-200 font-medium">€{displayedAnnual}</span>
        {launchActive && (
          <span className="ml-2 text-zinc-500 line-through decoration-zinc-700">
            €{block.standard}
          </span>
        )}
      </p>
      <div className="flex items-center flex-wrap gap-2 mt-2 text-xs">
        <span className="text-zinc-500 tabular-nums">
          <span className="text-[10px] font-mono uppercase tracking-wider text-zinc-500 mr-1">{t("pricing_cards.regular_price")}</span>
          <span className="line-through decoration-zinc-700">€{standardMonthly} {t("pricing_cards.per_month_slash")}</span>
        </span>
        {launchActive && (
          <span className="inline-flex items-center px-1.5 py-0.5 rounded font-mono uppercase tracking-wider bg-emerald-500/10 border border-emerald-500/25 text-emerald-300 text-[10px]">
            {t("pricing_cards.launch_price")}
          </span>
        )}
      </div>
      <p className="text-sm font-mono text-blue-400 mt-3">{t("pricing_cards.credits_per_month_short", { n: plan.credits })}</p>
    </div>
  );
}

function PlanCard({ plan, planName, planDesc, features, cycle, launchActive, billingEnabled }) {
  const { t } = useTranslation();
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const planId = `${plan.id}_${cycle}`;
  const label = billingEnabled ? t("pricing_cards.choose", { plan: planName }) : t("pricing_cards.coming_soon");

  async function handleSubscribe() {
    if (!billingEnabled || !consent || busy) return;
    setBusy(true);
    setError("");
    try {
      const token = localStorage.getItem("pf_token");
      if (!token) {
        window.location.href = `/signup?next=/pricing`;
        return;
      }
      const res = await axios.post(
        `${API}/billing/checkout`,
        { plan_id: planId, origin: window.location.origin },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (res?.data?.url) {
        window.location.href = res.data.url;
      } else {
        setError(res?.data?.message || t("pricing_cards.checkout_not_enabled"));
      }
    } catch (e) {
      setError(e?.response?.data?.detail || t("pricing_cards.checkout_generic_error"));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      data-testid={`pricing-card-${plan.id}-${cycle}`}
      className={`relative rounded-xl border p-7 sm:p-8 flex flex-col bg-[#12141C] ${
        plan.highlighted
          ? "border-blue-500/40 shadow-[0_0_60px_-20px_rgba(37,99,235,0.55)]"
          : "border-[#1E212B]"
      }`}
    >
      {plan.highlighted && (
        <span
          className="absolute -top-3 left-8 px-2 py-0.5 text-[10px] font-mono rounded bg-blue-500 text-white uppercase tracking-wider"
          data-testid={`pricing-most-popular-${plan.id}`}
        >
          {t("pricing_cards.most_popular")}
        </span>
      )}
      <h3 className="text-xl font-medium mb-1">{planName}</h3>
      <p className="text-xs text-zinc-500 mb-5 leading-relaxed">{planDesc}</p>

      <PriceBlock plan={plan} cycle={cycle} launchActive={launchActive} />

      <ul className="space-y-2 mb-8 flex-1">
        {features.map((f) => (
          <li key={f} className="flex items-start gap-2 text-sm text-zinc-300">
            <Check className="w-4 h-4 text-blue-400 mt-0.5 flex-shrink-0" />
            <span>{f}</span>
          </li>
        ))}
      </ul>

      {billingEnabled && (
        <label
          className="mb-3 flex items-start gap-2 text-xs text-zinc-400 leading-relaxed cursor-pointer select-none"
          data-testid={`pricing-consent-${plan.id}-${cycle}`}
        >
          <input
            type="checkbox"
            checked={consent}
            onChange={(e) => setConsent(e.target.checked)}
            className="mt-0.5 accent-blue-500"
            data-testid={`pricing-consent-checkbox-${plan.id}-${cycle}`}
          />
          <span>
            {t("pricing_cards.consent_prefix")}{" "}
            <Link to="/terms" className="text-blue-400 hover:text-blue-300 underline underline-offset-2">{t("pricing_cards.agb_link")}</Link>{" "}
            {t("pricing_cards.consent_and")}{" "}
            <Link to="/privacy" className="text-blue-400 hover:text-blue-300 underline underline-offset-2">{t("pricing_cards.privacy_link")}</Link>.{" "}
            <span className="text-zinc-500">{t("pricing_cards.consent_open_paren")}</span>
            <Link to="/cancellation" className="text-zinc-500 hover:text-zinc-300 underline underline-offset-2">{t("pricing_cards.cancellation_link")}</Link>
            <span className="text-zinc-500">{t("pricing_cards.consent_close_paren")}</span>
          </span>
        </label>
      )}

      <Button
        disabled={!billingEnabled || !consent || busy}
        aria-disabled={!billingEnabled || !consent || busy}
        onClick={billingEnabled ? handleSubscribe : undefined}
        data-testid={
          billingEnabled
            ? `pricing-subscribe-${plan.id}-${cycle}`
            : `pricing-coming-soon-${plan.id}`
        }
        className={`w-full ${(!billingEnabled || !consent) ? "cursor-not-allowed opacity-70" : ""} ${
          plan.highlighted
            ? "bg-blue-600/70 text-white hover:bg-blue-600"
            : "bg-[#1E212B] hover:bg-[#2A2E3B] text-white border border-[#2D313F]"
        }`}
      >
        {busy ? t("pricing_cards.redirecting") : label}
      </Button>
      {error && (
        <p
          className="mt-2 text-xs text-red-400"
          data-testid={`pricing-error-${plan.id}-${cycle}`}
        >
          {error}
        </p>
      )}
    </div>
  );
}

/**
 * @param {{ layout?: 'landing' | 'page' }} props
 */
export default function PricingCards({ layout = "page" }) {
  const { t } = useTranslation();
  const [cycle, setCycle] = useState("monthly");
  const launchActive = useLaunchActive();
  const billingEnabled = useBillingEnabled();

  // Resolve features arrays (i18n returnObjects) with a safe fallback.
  const starterFeatures = t("pricing_cards.features_starter", { returnObjects: true });
  const proFeatures = t("pricing_cards.features_pro", { returnObjects: true });

  return (
    <div className={layout === "landing" ? "" : ""}>
      <div className="flex flex-col items-center mb-10" data-testid="pricing-toggle">
        <div className="inline-flex items-center p-1 rounded-lg border border-[#1E212B] bg-[#12141C]">
          {[
            { id: "monthly", label: t("pricing_cards.monthly") },
            { id: "yearly",  label: t("pricing_cards.yearly") },
          ].map((opt) => {
            const active = cycle === opt.id;
            return (
              <button
                key={opt.id}
                type="button"
                data-testid={`pricing-toggle-${opt.id}`}
                onClick={() => setCycle(opt.id)}
                className={`px-4 sm:px-5 py-1.5 text-sm rounded-md transition-colors ${
                  active
                    ? "bg-blue-600 text-white"
                    : "text-zinc-400 hover:text-white"
                }`}
              >
                {opt.label}
                {opt.id === "yearly" && (
                  <span className="ml-2 text-[10px] font-mono uppercase tracking-wider text-emerald-300">
                    {t("pricing_cards.best_value")}
                  </span>
                )}
              </button>
            );
          })}
        </div>
        {cycle === "yearly" && (
          <p
            data-testid="pricing-yearly-billing-note"
            className="mt-3 text-xs text-zinc-500 font-mono"
          >
            {t("pricing_cards.yearly_billing_note")}
          </p>
        )}
      </div>

      <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
        <PlanCard
          plan={PLAN_DATA.starter}
          planName={t("pricing_cards.starter_name")}
          planDesc={t("pricing_cards.starter_desc")}
          features={Array.isArray(starterFeatures) ? starterFeatures : []}
          cycle={cycle}
          launchActive={launchActive}
          billingEnabled={billingEnabled}
        />
        <PlanCard
          plan={PLAN_DATA.pro}
          planName={t("pricing_cards.pro_name")}
          planDesc={t("pricing_cards.pro_desc")}
          features={Array.isArray(proFeatures) ? proFeatures : []}
          cycle={cycle}
          launchActive={launchActive}
          billingEnabled={billingEnabled}
        />
      </div>

      <div className="mt-8 text-xs text-zinc-500 text-center max-w-2xl mx-auto space-y-2">
        <p data-testid="pricing-credit-policy-note" className="text-zinc-400">
          {t("pricing_cards.credit_policy_note")}
        </p>
        <p data-testid="pricing-vat-note">
          {t("pricing_cards.vat_note")}
        </p>
        {!billingEnabled && (
          <p>
            {t("pricing_cards.checkout_unavailable")}
          </p>
        )}
      </div>
    </div>
  );
}
